import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchUpcomingAdjustments,
  fetchUpcomingAdminAdjustments
} from '../../../Redux/Actions/actions';
import {
  FaChartLine,
  FaCalendarAlt,
  FaHome,
  FaUser
} from 'react-icons/fa';
import {
  RENTAL_TYPE,
  formatCurrency,
  formatDate,
  getDetailPath
} from '../utils/rentalTypeHelpers';
import style from './UpcomingAdjustments.module.css';

const DAYS_WINDOW = 30;

const getAdjustmentDate = (item) =>
  item?.nextAdjustmentDate ||
  item?.appliedFrom ||
  item?.rental?.nextAdjustmentDate ||
  item?.rental?.nextAdjustment ||
  null;

const getRental = (item) => item?.rental || item;

const getDaysToAdjustment = (dateString) => {
  if (!dateString) return null;
  const target = new Date(dateString);
  if (Number.isNaN(target.getTime())) return null;
  const now = new Date();
  const diffTime = target - now;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const UpcomingAdjustments = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    upcomingAdjustments,
    upcomingAdminAdjustments,
    rentalsLoading,
    adminRentalsLoading,
    rentalsError,
    adminRentalsError
  } = useSelector((state) => state);
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    dispatch(fetchUpcomingAdjustments(DAYS_WINDOW));
    dispatch(fetchUpcomingAdminAdjustments(DAYS_WINDOW));
  }, [dispatch]);

  const combined = useMemo(() => {
    const reg = (upcomingAdjustments || []).map((item) => ({
      _type: RENTAL_TYPE.REGULAR,
      raw: item
    }));
    const adm = (upcomingAdminAdjustments || []).map((item) => ({
      _type: RENTAL_TYPE.ADMIN,
      raw: item
    }));
    return [...reg, ...adm];
  }, [upcomingAdjustments, upcomingAdminAdjustments]);

  const adjustmentsThisWindow = useMemo(() => {
    return combined.filter(({ raw }) => {
      const dateString = getAdjustmentDate(raw);
      if (!dateString) return false;
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return false;
      const now = new Date();
      const diffTime = date - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= DAYS_WINDOW;
    });
  }, [combined]);

  const filtered = useMemo(() => {
    if (typeFilter === 'all') return adjustmentsThisWindow;
    return adjustmentsThisWindow.filter((it) => it._type === typeFilter);
  }, [adjustmentsThisWindow, typeFilter]);

  const isLoading = rentalsLoading || adminRentalsLoading;
  const errorMessage =
    rentalsError && adminRentalsError ? rentalsError : null;

  if (isLoading && combined.length === 0) {
    return (
      <div className={style.loading}>Cargando ajustes próximos...</div>
    );
  }
  if (errorMessage) {
    return <div className={style.error}>Error: {errorMessage}</div>;
  }

  const sorted = [...filtered].sort((a, b) => {
    const dateA = new Date(getAdjustmentDate(a.raw) || 0);
    const dateB = new Date(getAdjustmentDate(b.raw) || 0);
    return dateA - dateB;
  });

  return (
    <div className={style.container}>
      <div className={style.header}>
        <h1 className={style.title}>
          <FaChartLine className={style.titleIcon} />
          Ajustes Programados (próximos {DAYS_WINDOW} días)
        </h1>
        <div className={style.subtitle}>
          Se muestran contratos con ajuste de cuota dentro de los próximos{' '}
          {DAYS_WINDOW} días.
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: '12px 0',
          flexWrap: 'wrap'
        }}
      >
        {[
          { v: 'all', l: 'Todos' },
          { v: RENTAL_TYPE.REGULAR, l: 'Regular' },
          { v: RENTAL_TYPE.ADMIN, l: 'Solo Admin' }
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => setTypeFilter(opt.v)}
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              background: typeFilter === opt.v ? '#1e40af' : '#e5e7eb',
              color: typeFilter === opt.v ? '#fff' : '#374151',
              fontWeight: 600,
              fontSize: '0.85rem'
            }}
          >
            {opt.l}
          </button>
        ))}
      </div>

      <div className={style.summary}>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>
            Total Ajustes ({DAYS_WINDOW} días)
          </span>
          <span className={style.summaryValue}>{filtered.length}</span>
        </div>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Ventana de días</span>
          <span className={style.summaryValue}>{DAYS_WINDOW}</span>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className={style.noData}>
          <FaChartLine className={style.noDataIcon} />
          <h3>No hay ajustes programados en los próximos {DAYS_WINDOW} días</h3>
          <p>Probá revisar más adelante.</p>
        </div>
      ) : (
        <div className={style.list}>
          {sorted.map(({ _type, raw }) => {
            const rental = getRental(raw);
            const property = rental?.property || raw?.property;
            const adjustmentDate = getAdjustmentDate(raw);
            const daysToAdjustment = getDaysToAdjustment(adjustmentDate);
            const mainTenant = rental?.tenants?.find(
              (t) => t.tenantType === 'main'
            );
            const isAdmin = _type === RENTAL_TYPE.ADMIN;
            const propAddress = property
              ? `${property.street || ''} ${property.number || ''}, ${
                  property.locality || ''
                }`.trim()
              : rental?.propertyAddress || '—';
            const propTitle =
              property?.title || rental?.propertyTitle || 'Propiedad sin título';
            const contractId = rental?.id || raw?.id;

            return (
              <div
                key={`${_type}-${raw.id || contractId}`}
                className={style.card}
              >
                <div className={style.cardHeader}>
                  <div className={style.propertyInfo}>
                    <FaHome className={style.propertyIcon} />
                    <div>
                      <h3 className={style.propertyTitle}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            background: isAdmin ? '#fef3c7' : '#dbeafe',
                            color: isAdmin ? '#92400e' : '#1e40af',
                            marginRight: 8,
                            verticalAlign: 'middle'
                          }}
                        >
                          {isAdmin ? 'Admin' : 'Regular'}
                        </span>
                        {propTitle}
                      </h3>
                      <p className={style.propertyAddress}>{propAddress}</p>
                    </div>
                  </div>
                  <div className={style.badge}>
                    <FaCalendarAlt />
                    {formatDate(adjustmentDate)}
                    {Number.isFinite(daysToAdjustment) && (
                      <span className={style.badgeSubtext}>
                        {daysToAdjustment >= 0
                          ? `En ${daysToAdjustment} días`
                          : 'Vencido'}
                      </span>
                    )}
                  </div>
                </div>

                <div className={style.cardContent}>
                  <div className={style.infoGrid}>
                    <div className={style.infoItem}>
                      <FaUser className={style.infoIcon} />
                      <div>
                        <span className={style.infoLabel}>
                          Inquilino Principal
                        </span>
                        <span className={style.infoValue}>
                          {mainTenant?.fullName || 'No asignado'}
                        </span>
                      </div>
                    </div>

                    <div className={style.infoItem}>
                      <span className={style.infoLabel}>Tipo de Ajuste</span>
                      <span className={style.infoValue}>
                        {(
                          rental?.adjustmentType ||
                          raw?.adjustmentType ||
                          'ICL'
                        ).toUpperCase()}
                      </span>
                    </div>

                    <div className={style.infoItem}>
                      <span className={style.infoLabel}>Frecuencia</span>
                      <span className={style.infoValue}>
                        Cada{' '}
                        {rental?.adjustmentFrequency ||
                          raw?.adjustmentFrequency ||
                          12}{' '}
                        meses
                      </span>
                    </div>

                    <div className={style.infoItem}>
                      <span className={style.infoLabel}>Alquiler Actual</span>
                      <span className={style.infoValue}>
                        {formatCurrency(
                          rental?.currentRentValue ||
                            rental?.rentValue ||
                            raw?.currentRentValue
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={style.cardActions}>
                  <button
                    className={`${style.actionButton} ${style.secondary}`}
                    onClick={() =>
                      navigate(
                        getDetailPath(contractId, _type, 'ajustes')
                      )
                    }
                  >
                    Gestionar Ajustes
                  </button>
                  <button
                    className={`${style.actionButton} ${style.secondary}`}
                    onClick={() =>
                      navigate(getDetailPath(contractId, _type, 'pagos'))
                    }
                  >
                    Gestionar Pagos
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UpcomingAdjustments;
